// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  User,
  getOrganizationUsers,
} from '@/account/api/get-organization-users';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { BaseSearchableFilter } from '@/filters/base-searchable-filter';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

interface Props {
  /**
   * An array of strings representing the currently selected person IDs.
   */
  value: string[];

  /**
   * The label for the filter menu.
   */
  label?: string;

  /**
   * A callback fired when the selected person IDs change.
   */
  onChange: (value: string[]) => void;

  /**
   * Whether to show the search input. Defaults to true.
   */
  enableSearch?: boolean;

  /**
   * Whether to show a "Not set" option as the first item. When selected,
   * it will pass "null" as an ID to the server. Defaults to false.
   */
  enableNotSet?: boolean;
}

/**
 * A searchable filter for selecting zero or more persons from the remote API.
 */
export function PersonFilter(props: Props) {
  const { user } = useCurrentUser();
  const { data: usersResponse, isLoading } = useQuery({
    queryKey: ['organization-users', user.organization.key],
    queryFn: () => getOrganizationUsers(user.organization.key),
  });

  // Memoized on the response; deriving the user list inside keeps the fresh
  // array it produces out of the dependencies
  const options = useMemo(() => {
    const users =
      usersResponse?.users
        .filter(
          (person: User) =>
            !person.isDeactivated &&
            person.firstName !== null &&
            person.lastName !== null
        )
        .sort((a, b) => {
          const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
          const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
          return nameA.localeCompare(nameB);
        }) ?? [];

    const userOptions = users.map((person: User) => ({
      value: person.id,
      label: `${person.firstName} ${person.lastName}`,
    }));

    return props.enableNotSet
      ? [{ value: 'null', label: 'Not set', isSpecial: true }, ...userOptions]
      : userOptions;
  }, [usersResponse, props.enableNotSet]);

  const handleChangeOption = (id: string, isActive: boolean) => {
    if (isActive) {
      return props.onChange([...props.value, id]);
    }

    props.onChange(props.value.filter((personId) => personId !== id));
  };

  return (
    <BaseSearchableFilter
      label={props.label ?? 'People'}
      options={options}
      selectedValues={props.value}
      onChangeOption={handleChangeOption}
      searchPlaceholder="Search people..."
      enableSearch={props.enableSearch}
      isLoading={isLoading}
    />
  );
}
